import type { NFTI } from '@tacans/shared-marketplace-layer';
import { UserCurrency } from '@tacans/shared-marketplace-layer';
import { useMemo } from 'react';
import { FIAT_FR_DIGITS_LENGTH } from 'src/constants/common.constants';
import { useAuthorized } from 'src/features/common/auth/hooks/auth.hooks';
import { NftSaleDetails } from 'src/features/common/collectible/collectible.typing';
import { useAllCurrencyEntities } from 'src/features/common/currency/hooks/currency.hooks';
import { useFiatCurrency } from 'src/features/common/profile/hooks/profile.hooks';
import { useCoins } from 'src/features/common/wallet/hooks/wallet.hooks';
import { formatCurrency, makePriceByNano } from 'src/utils/entity.utils';

interface INftCardLocale {
    notForSale: string;
    notMinted: string;
    burnt: string;
}

const getCollectiblePrice = (collectible: NFTI, locale: INftCardLocale, nftSales?: NftSaleDetails): string => {
    const { notForSale } = locale;
    const { activeLotsCount, nanoPrice, nanoBid } = nftSales || {};
    const nano = nanoBid || nanoPrice;
    if (!activeLotsCount) return notForSale;
    if (!nano) {
        return '';
    }
    return formatCurrency(makePriceByNano(nano), undefined);
};

const getCollectibleFiatPrice = (
    collectible: NFTI,
    nftSales: NftSaleDetails,
    multiplier: number,
    isAuthorized?: boolean
): string => {
    //   if (!isAuthorized) return '';

    const { activeLotsCount } = nftSales;

    const nano = nftSales.nanoBid || nftSales.nanoPrice;

    if (!activeLotsCount || !nano) return '';

    return formatCurrency(makePriceByNano(nano * multiplier), FIAT_FR_DIGITS_LENGTH);
};

export const useCollectiblePrice = (collectible: NFTI, locale: INftCardLocale, nftSales?: NftSaleDetails) => {
    const isAuthorized = useAuthorized();
    const allCurrencyEntities = useAllCurrencyEntities();
    const coins = useCoins();
    const coinId = coins[collectible.blockchainId]?.[0]?.id;

    const fiatCurrency = useFiatCurrency();

    const price = useMemo(() => getCollectiblePrice(collectible, locale, nftSales), [collectible, nftSales, locale]);

    const multiplier =
        fiatCurrency === UserCurrency.EUR ? allCurrencyEntities[coinId]?.euro : allCurrencyEntities[coinId]?.usd;

    const fiatPrice = useMemo(
        () => (nftSales?.isOnSale ? getCollectibleFiatPrice(collectible, nftSales, multiplier || 1, isAuthorized) : ''),
        [nftSales, collectible, multiplier, isAuthorized]
    );

    return useMemo(
        () => ({
            fiatCurrency,
            price,
            fiatPrice,
        }),
        [fiatCurrency, fiatPrice, price]
    );
};
